/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: authoring-common
 * (C) Copyright IBM Corp. 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
	Accordion,
	AccordionItem,
	OverflowMenu,
	OverflowMenuItem
} from '@ba/ba-ui-carbon';
import {
	FlexLayout,
	FlexItem,
	Label
} from '@ba/ba-ui-carbon-toolkit';

import StringResources from 'nls/StringResources';
import CustomPalette from '../dialog/CustomPalette';
import DialogWrapper from '../utilities/DialogWrapper';

export default class PaletteListing extends Component{

	// these are the anticipated props based on the ChangePaletteView options
	static propTypes = {
		paletteService: PropTypes.object.isRequired, // was glass context
		getPalettes: PropTypes.func.isRequired,
		onCreatePalette: PropTypes.func.isRequired,
		onChange: PropTypes.func.isRequired,
		mruComponentIdentifier: PropTypes.string.isRequired, // was component
		selectedId: PropTypes.string,
		createPaletteType: PropTypes.string,
		showRecentlyUsed: PropTypes.bool,
		createGlobalPalettes: PropTypes.bool, // was isAdminUI
		sectionMenuItems: PropTypes.object // was menuItems
	}

	static defaultProps = {
		sectionMenuItems: {
			'my': ['edit', 'duplicate', 'delete'],
			'local': ['duplicate'],
			'public': ['duplicate'],
			'system': ['duplicate'],
			'legacy': []
		}
	}

	constructor(props) {
		super(props);
		this.state = {
			palettes: null
		};
	}

	componentDidMount(){
		this._registerPaletteServiceEvents();
		this.props.getPalettes().then( ( palettes ) => {
			this.setState({
				palettes: this._removeGlobalFromCustom(palettes),
				selectedId: this.props.selectedId
			});
		});
	}

	componentWillUnmount(){
		this._unregisterPaletteServiceEvents();
	}

	render(){
		if ( !this.state.palettes ) return null;
		const sections = [];
		for ( let section in this.state.palettes ){
			let open = false;
			let palettes = this.state.palettes[section].map(( palette ) => {
				let fills = palette.content.fills.map( ( fill ) => {
					if ( this.props.createPaletteType === 'continuous' ){
						fill = fill.fill;
					}
					return (<FlexItem className='clsPaletteListingBoxStyle' style={{ backgroundColor: fill }} key={fill}/>);
				});
				let className = 'clsPaletteListingPalette';
				if ( palette.id === this.state.selectedId ) {
					className += '_selected';
					open = true;
				}
				const menuItems = this.props.sectionMenuItems[ section ];
				let overFlow = null;
				if ( menuItems && menuItems.length ) {
					overFlow = (
						<FlexItem>
							<OverflowMenu>
								{ menuItems.map( (menuItem) => {
									return (
										<OverflowMenuItem
											key={menuItem}
											itemText={StringResources.get(menuItem)}
											onClick={ ( event ) => this._performPaletteAction( event, palette, menuItem )}
										/>);
								})}
							</OverflowMenu>
						</FlexItem>
					);
				}
				return (
					<div key={palette} className={className} onClick={this._selectPalette.bind(this, palette)}>
						<FlexLayout justifyContent='space-between' alignItems='center'>
							<FlexItem>
								<Label label={palette.content.label}/>
							</FlexItem>
							{overFlow}
						</FlexLayout>
						<FlexLayout wrap='wrap'>
							{fills}
						</FlexLayout>
					</div>
				);
			});
			if ( palettes.length ){
				sections.push( (
					<AccordionItem open={open} title={StringResources.get(section + 'PaletteLabel')} className='clsPaletteListingAccordianItem'>
						{palettes}
					</AccordionItem>
				));
			}
		}
		return (
			<Accordion>
				{sections}
			</Accordion>
		);
	}

	_registerPaletteServiceEvents() {
		if (!this._registered) {
			this.props.paletteService.on('palette:created', this._onPaletteCreated, this);
			this.props.paletteService.on('palette:updated', this._onPaletteUpdate, this);
			this.props.paletteService.on('palette:deleted', this._onPaletteDelete, this);
			this._registered = true;
		}
	}

	_unregisterPaletteServiceEvents() {
		if (this._registered) {
			this.props.paletteService.off('palette:created', this._onPaletteCreated, this);
			this.props.paletteService.off('palette:updated', this._onPaletteUpdate, this);
			this.props.paletteService.off('palette:deleted', this._onPaletteDelete, this);
		}
	}

	_selectPalette( palette ){
		this.setState({ selectedId: palette.id });
		this.props.onChange( null, palette );
	}

	_onPaletteUpdate( options ){
		if ( options.paletteId === this.state.selectedId ){
			this.props.onChange( null, this._findPaletteFromId( options.paletteId ));
		}
	}

	_onPaletteDelete( options ){
		const palettes = { ...this.state.palettes };
		let matchedSection = null;
		for ( let sectionName in palettes ){
			const sectionPalettes = palettes[sectionName];
			const matchedPalette = sectionPalettes.find( (candidatePalette) => candidatePalette.id == options.paletteId );
			if ( matchedPalette ) {
				palettes[sectionName] = sectionPalettes.filter( (p) => p != matchedPalette );
				matchedSection = sectionName;
				break;
			}
		}
		if ( options.paletteId != this.state.selectedId ){
			this.setState( { palettes: palettes });
		} else {
			const section = matchedSection || 'system';
			const palette = palettes[section].length ? palettes[section][0] : palettes['system'][0];
			this.setState( { palettes: palettes, selectedId: palette.id });
			this.props.onChange( null, palette );
		}
	}

	_onPaletteCreated( options ){
		this.props.paletteService.getPalette( options.paletteId ).then( (palette) => {
			const palettes = { ...this.state.palettes };
			// can we really know which section to put it in? sections can be overriden, may not even exist...
			if ( palette.public ) {
				palettes.public.push( palette );
			} else if ( palette.my ) {
				palettes.my.push( palette );
			} else {
				// can this even happen?  maybe they should go on local
			}
			this.setState({
				palettes: palettes,
				selectedId: palette.id
			});
			this.props.onChange( null, palette );
		});
	}

	_findPaletteFromId( paletteId ){
		for ( let sectionName in this.state.palettes ){
			const matchedPalette = this.state.palettes[sectionName].find( (candidatePalette) => candidatePalette.id == paletteId );
			if ( matchedPalette ) {
				return matchedPalette;
			}
		}
	}

	_removeGlobalFromCustom( palettes ){
		if ( !palettes.my ) {
			return palettes;
		}
		palettes.my = palettes.my.filter( (palette) => { return !palette.public; });
		return palettes;
	}

	_performPaletteAction( _event, palette, action ){
		switch( action ){
			case 'edit':
				DialogWrapper.show( CustomPalette, {
					paletteService: this.props.paletteService,
					paletteDef: palette.content,
					paletteId: palette.id,
					isDialogOpen: true,
					type: this.props.createGlobalPalettes ? 'global' : '',
					userClass: this.props.createGlobalPalettes ? 'admin' : '',
					paletteType: palette.content.type === 'continuous' ? 'continuous' : 'standard'
				});
				break;
			case 'delete':
				this.props.paletteService.deletePalette( palette.id );
				break;
			case 'duplicate':
				DialogWrapper.show( CustomPalette, {
					paletteService: this.props.paletteService,
					paletteDef: palette.content,
					isDialogOpen: true,
					type: this.props.createGlobalPalettes ? 'global' : '',
					userClass: this.props.createGlobalPalettes ? 'admin' : '',
					paletteType: palette.content.type === 'continuous' ? 'continuous' : 'standard'
				});
				break;
			default:
				break;
		}
	}
}


// WEBPACK FOOTER //
// ./src/js/components/PaletteListing.js